import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import * as Collapsible from '@radix-ui/react-collapsible'

import { useOnEvent } from '@sketch/utils'
import {
  useModalContext,
  SelectDropdownActionDropdown,
} from '@sketch/components'
import {
  ShareDropPayload,
  ProjectDropPayload,
  DroppableLinkItem,
  RouteParams,
} from '@sketch/modules-common'
import { ConfirmStorageItemTransferModalDragged } from './ConfirmStorageItemTransferModalDragged'

import { RenameInlineEditor } from '../InlineEditor'
import ProjectDropdown from '../ProjectDropdown'

import {
  CollapsibleButton,
  ChevronRight,
  ChevronDown,
  NestedProjectsWrapper,
  TouchOnlyCollapsibleIcon,
  TouchOnlyCollapsibleWrapper,
} from './ProjectListItem.styles'

import { ReactComponent as PinIcon } from '@sketch/icons/pushpin-17'
import { ReactComponent as MyDraftsIcon } from '@sketch/icons/notes-17'
import { ReactComponent as FolderClosedIcon } from '@sketch/icons/folder-closed-17'
import { ReactComponent as FolderOpenIcon } from '@sketch/icons/folder-open-17'

import { ProjectProps } from './ProjectListItem.types'

import { privacyIcon, hasProject } from './ProjectListItem.utils'

import { useTransfer } from './ProjectListItem.hooks'
import { isMyDrafts } from '../../utils'

export const ProjectListItem: React.FC<ProjectProps> = props => {
  const {
    link,
    project,
    nestedProjects,
    workspace,
    refToLast,
    setEditedItem,
    forceHighlight = false,
    isProjectList,
    onOpenChange,
    defaultOpen,
    depth = 0,
    topLevelProject,
    isDescendantOf,
  }: typeof props = props

  const { showModal } = useModalContext()
  const [isRenaming, setIsRenaming] = useState(false)
  const [isDroppableAreaEnabled, setIsDroppableAreaEnabled] = useState(false)

  const { projectId: openedProject } =
    useParams<RouteParams<'WORKSPACE_PROJECT'>>()

  // An active nested projects is expanded by default
  const [nestedOpen, _setNestedOpen] = React.useState(
    hasProject(nestedProjects, openedProject) || defaultOpen(project.identifier)
  )

  const setNestedOpen = useCallback(
    (open: boolean) => {
      _setNestedOpen(open)
      onOpenChange(project.identifier, open)
    },
    [onOpenChange, project.identifier]
  )

  const location = useLocation()

  // Let's make sure we show the active project item when the route changes
  useEffect(() => {
    if (hasProject(nestedProjects, openedProject)) {
      setNestedOpen(true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // We want to show expanded newly created nested projects
  useOnEvent('nestedProjectCreated', ({ projectId }) => {
    if (hasProject(nestedProjects, projectId)) {
      setNestedOpen(true)
    }
  })

  const { move } = useTransfer({
    workspaceId: workspace.identifier,
    newProject: project,
  })

  const onShareDragOver = ({ share }: ShareDropPayload) => {
    if (share?.project?.identifier === project.identifier) {
      setIsDroppableAreaEnabled(false)
    } else {
      setIsDroppableAreaEnabled(true)
    }
  }

  const onProjectDragOver = ({
    project: projectDragged,
  }: ProjectDropPayload) => {
    if (!projectDragged?.identifier) return false

    if (
      projectDragged?.parentProjectIdentifier === project.identifier ||
      projectDragged?.identifier === project.identifier ||
      isDescendantOf(projectDragged.identifier, project.identifier)
    ) {
      setIsDroppableAreaEnabled(false)
    } else {
      setIsDroppableAreaEnabled(true)
    }
  }

  const onShareDropped = ({ share }: ShareDropPayload) => {
    /**
     * No share nothing to move ¯\_(ツ)_/¯
     */
    if (!share) return

    /**
     * Nothing to move if you are trying to move a share to the same project
     */
    if (
      share.project?.identifier &&
      share.project?.identifier === project.identifier
    ) {
      return
    }

    /**
     * If the project shares the same top level project
     * we can move the document without any confirmation
     * as the sharing permissions are the same
     */
    if (
      (project.topLevelProject || project)?.identifier ===
      (share.project?.topLevelProject || share.project)?.identifier
    ) {
      move(share)
      return
    }

    showModal(ConfirmStorageItemTransferModalDragged, {
      item: share,
      workspace,
      destinationProject: project,
    })
  }

  const onProjectDropped = async ({
    project: projectDragged,
  }: ProjectDropPayload) => {
    const projectDropTarget = project

    if (!projectDragged) return

    // We don't want to move the project into the folder project
    if (
      projectDragged.parentProjectIdentifier === projectDropTarget.identifier
    ) {
      return
    }

    // We don't want to move the project into itself
    if (projectDragged.identifier === projectDropTarget.identifier) {
      return
    }

    // We don't want to move the project into one of its descendants
    if (isDescendantOf(projectDragged.identifier, project.identifier)) {
      return
    }

    /**
     * If the folder shares the same top level project
     * we can move the folder without any confirmation
     * as the sharing permissions are the same
     */
    if (
      (projectDragged.topLevelProject || projectDragged)?.identifier ===
      (projectDropTarget?.topLevelProject || projectDropTarget)?.identifier
    ) {
      move(projectDragged)
      return
    }

    showModal(ConfirmStorageItemTransferModalDragged, {
      item: projectDragged,
      destinationProject: projectDropTarget,
      workspace,
    })
  }

  const workspaceId = workspace.identifier

  const toggleEditor = () => setIsRenaming(!isRenaming)
  const onCompleted = () => {
    setEditedItem?.(project.identifier)
  }

  // We should show the pin icon when FF is enabled
  // when the project is pinned and when it's not
  // a private project, as the lock icon should prevail
  // https://www.sketch.com/s/d57ba117-58de-4a49-b217-0720404f0a08/a/oM73ayj
  const showPinIcon =
    depth === 0 &&
    !isProjectList &&
    project.pinnedByCurrentUserAt &&
    project.projectPrivacyIcon !== 'INVITED_WORKSPACE_MEMBERS'

  // This function decides which icon needs to be shown for the project
  const getIcon = () => {
    const nestedProjectIcons = {
      ...(nestedProjects.length && {
        hoverIcon: nestedOpen ? ChevronDown : ChevronRight,
      }),
      ...(nestedProjects.length && {
        hoverIconActive: nestedOpen ? ChevronDown : ChevronRight,
      }),
    }

    if (project.projectType === 'PERSONAL_DRAFTS') {
      if (project.parentProjectIdentifier) {
        const icon = nestedOpen ? FolderOpenIcon : FolderClosedIcon
        return {
          icon,
          activeIcon: icon,
          label: 'My Drafts',
          ...nestedProjectIcons,
        }
      }
      return {
        icon: MyDraftsIcon,
        activeIcon: MyDraftsIcon,
        label: 'My Drafts',
        ...nestedProjectIcons,
      }
    }

    if (showPinIcon) {
      return {
        icon: PinIcon,
        activeIcon: PinIcon,
        label: 'Pin icon',
        ...nestedProjectIcons,
      }
    }

    // Sharing settings can be set only on the top level project and all nested projects inherit the same settings
    // Therefore privacy icons should be always based on the top level project
    // See: https://linear.app/sketch/issue/SWEB-1490/sidebar-icon-updates-on-top-folder-but-not-on-nested-folders
    const icon = privacyIcon(topLevelProject || project, nestedOpen)

    return {
      icon: icon.component,
      activeIcon: icon.component,
      label: icon.label,
      ...nestedProjectIcons,
    }
  }

  const Trigger: React.FC = ({ children }) => (
    <Collapsible.Trigger asChild>
      <CollapsibleButton>{children}</CollapsibleButton>
    </Collapsible.Trigger>
  )
  const icon = getIcon()

  const hasChildren = nestedProjects.length > 0

  // The only project list item that shouldn't be draggable in the sidebar is
  // the "My Drafts" project, as it is a special project
  const shouldBeDraggable = !isMyDrafts(project)

  const linkItem = isRenaming ? (
    <RenameInlineEditor
      depth={depth + 1}
      project={project}
      workspaceId={workspaceId}
      placeholder={project.name || 'Folder'}
      toggleEditor={toggleEditor}
      onCompleted={onCompleted}
    />
  ) : (
    <DroppableLinkItem
      draggable={shouldBeDraggable}
      projectIdentifier={project.identifier}
      isDroppableAreaEnabled={isDroppableAreaEnabled}
      icon={icon}
      iconLabel={icon.label}
      {...(hasChildren && { iconWrapper: Trigger })}
      to={link}
      text={project.name}
      onShareDropped={onShareDropped}
      onProjectDropped={onProjectDropped}
      onShareDragOver={onShareDragOver}
      onProjectDragOver={onProjectDragOver}
      onUnknownDragOver={() => setIsDroppableAreaEnabled(false)}
      action={
        <SelectDropdownActionDropdown
          text="Rename & More…"
          content={
            <ProjectDropdown
              workspace={workspace}
              project={project}
              onRename={() => setIsRenaming(!isRenaming)}
            />
          }
        />
      }
      ref={refToLast}
      aria-label={`Link to ${project.name}`}
      forceHighlight={forceHighlight}
      additional={
        <TouchOnlyCollapsibleWrapper
          onClick={e => {
            e.stopPropagation()
            e.preventDefault()
            setNestedOpen(!nestedOpen)
          }}
          disabled={!hasChildren}
        >
          <TouchOnlyCollapsibleIcon $open={nestedOpen} />
        </TouchOnlyCollapsibleWrapper>
      }
    />
  )

  return nestedProjects.length ? (
    <Collapsible.Root open={nestedOpen} onOpenChange={setNestedOpen}>
      {linkItem}
      <Collapsible.Content>
        <NestedProjectsWrapper level={depth + 1}>
          {nestedProjects.map(projectLink => (
            <ProjectListItem
              topLevelProject={topLevelProject}
              key={projectLink.project.identifier}
              project={projectLink.project}
              link={projectLink.link}
              nestedProjects={projectLink.nestedProjects}
              workspace={workspace}
              refToLast={refToLast}
              setEditedItem={setEditedItem}
              forceHighlight={forceHighlight}
              isProjectList={isProjectList}
              onOpenChange={onOpenChange}
              defaultOpen={defaultOpen}
              depth={depth + 1}
              isDescendantOf={isDescendantOf}
            />
          ))}
        </NestedProjectsWrapper>
      </Collapsible.Content>
    </Collapsible.Root>
  ) : (
    linkItem
  )
}
